/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/mv_intersects.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Checks if any value yielded by the second multivalue expression is present in the values yielded by the first multivalue expression. Returns a boolean. Null values are treated as an empty set.
 *
 * @example
 * ROW set = ["a", "b", "c"], element = "a"
 * | EVAL set_intersects_element = mv_intersects(set, element)
 *
 * @example
 * ROW setA = ["a","c"], setB = ["a", "b", "c"]
 * | EVAL a_intersects_b = mv_intersects(setB, setA)
 * | EVAL b_intersects_a = mv_intersects(setA, setB)
 *
 * @example
 * FROM airports
 * | WHERE mv_intersects(type, ["major"]) AND scalerank == 9
 * | KEEP scalerank, name, country
 * | SORT country
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'mv_intersects',
  signatures: [
    {
      params: [
        {
          name: 'field1',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'field2',
          type: 'boolean',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'field2',
          type: 'cartesian_point',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'field2',
          type: 'cartesian_shape',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'date',
          optional: false,
        },
        {
          name: 'field2',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'field2',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'double',
          optional: false,
        },
        {
          name: 'field2',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'field2',
          type: 'flattened',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'field2',
          type: 'geo_point',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'field2',
          type: 'geo_shape',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'geohash',
          optional: false,
        },
        {
          name: 'field2',
          type: 'geohash',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'geohex',
          optional: false,
        },
        {
          name: 'field2',
          type: 'geohex',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'geotile',
          optional: false,
        },
        {
          name: 'field2',
          type: 'geotile',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'integer',
          optional: false,
        },
        {
          name: 'field2',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'ip',
          optional: false,
        },
        {
          name: 'field2',
          type: 'ip',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'field2',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'field2',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'long',
          optional: false,
        },
        {
          name: 'field2',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'text',
          optional: false,
        },
        {
          name: 'field2',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'text',
          optional: false,
        },
        {
          name: 'field2',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'field2',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field1',
          type: 'version',
          optional: false,
        },
        {
          name: 'field2',
          type: 'version',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
  ],
  preview: true,
  snapshotOnly: false,
};

export default definition;
